/**
 * The facility's page for a secure records link (docs/facility-delivery/DESIGN.md 2.1, option A). PUBLIC: no login.
 *
 *   1. The email's link is /records#<token>. The token is read from the URL FRAGMENT (never sent to any server by the
 *      browser) and the fragment is cleared from the address bar at once; the token then lives in this page's memory.
 *   2. "Open" shows the building, the visit day and how many files -- the facts the email already carried.
 *   3. The recipient types their email address; if it is one the records were sent to, a 6-digit code goes there.
 *   4. With the code: the files, each downloadable a limited number of times, and "Confirm received".
 *
 * Every request is a POST with the token in the BODY, made with fetch and credentials 'omit': this page never sends
 * the staff session (no Authorization header, no cookie) and never goes through the portal's shared client. No
 * resident name is shown before the code. Phone-first.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/shadcn/ui/button';
import { Input } from '../../components/shadcn/ui/input';
import config from '../../config';

/** The same API base as the portal (config.apiBaseUrl), without the portal's client or its session. */
const API = `${String(config.apiBaseUrl || '/api/v1').replace(/\/$/, '')}/records-link`;

type Answer<T> = { ok: boolean; status: number; data?: T; code?: string; message?: string };

async function post<T>(path: string, body: Record<string, unknown>): Promise<Answer<T>> {
  try {
    const res = await fetch(`${API}${path}`, {
      method: 'POST', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data: json.data, code: json.code, message: json.message };
  } catch {
    return { ok: false, status: 0, message: 'Could not reach Sounds for Seniors. Check the connection and try again.' };
  }
}

async function postFile(path: string, body: Record<string, unknown>): Promise<{ ok: boolean; blob?: Blob; name?: string; message?: string }> {
  try {
    const res = await fetch(`${API}${path}`, {
      method: 'POST', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!res.ok) { const json = await res.json().catch(() => ({})); return { ok: false, message: json.message || 'The file could not be downloaded.' }; }
    const cd = res.headers.get('content-disposition') || '';
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    return { ok: true, blob: await res.blob(), name: star ? decodeURIComponent(star[1]) : 'record.pdf' };
  } catch {
    return { ok: false, message: 'Could not reach Sounds for Seniors.' };
  }
}

type Opened = { building_name: string; visit_date: string; file_count: number; expires_at: string; locked: boolean; locked_until?: string | null; code_minutes: number };
type FileRow = { file_no: number; name: string; kind: string; byte_size: number; downloads_left: number; available: boolean };

const usDate = (ymd: string) => (/^\d{4}-\d{2}-\d{2}$/.test(ymd) ? `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}` : ymd);
const size = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`);

export function readToken(hash: string): string | null {
  const t = String(hash || '').replace(/^#/, '').trim();
  return /^[A-Za-z0-9_-]{43}$/.test(t) ? t : null;
}

export default function RecordsLink() {
  const token = useRef<string | null>(null);
  const [stage, setStage] = useState<'loading' | 'unavailable' | 'email' | 'code' | 'files'>('loading');
  const [info, setInfo] = useState<Opened | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState<string | null>(null);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    token.current = readToken(window.location.hash);
    // The token never stays in the address bar or the history entry.
    try { window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`); } catch { /* ignore */ }
    if (!token.current) { setStage('unavailable'); return; }
    post<Opened>('/open', { token: token.current }).then((r) => {
      if (r.ok && r.data) {
        setInfo(r.data); setStage(r.data.locked ? 'unavailable' : 'email');
        // A pause, not an end (2026-10-28): say when it can be tried again.
        if (r.data.locked) setError(`This link is paused for an hour after too many wrong codes.${r.data.locked_until ? ` Try again after ${new Date(r.data.locked_until).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.` : ''}`);
      } else { setStage('unavailable'); setError(r.message || null); }
    });
  }, []);

  const askCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const r = await post('/code', { token: token.current, email: email.trim() });
    setBusy(false);
    if (r.ok) { setMessage(r.message || 'If that is the address these records were sent to, a code is on its way.'); setStage('code'); } else setError(r.message || 'That did not work.');
  };
  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    // The code works only with the address it was mailed to, so the address goes with it.
    const r = await post<{ session: string; files: FileRow[] }>('/verify', { token: token.current, email: email.trim(), code: code.trim() });
    setBusy(false);
    if (r.ok && r.data) { setSession(r.data.session); setFiles(r.data.files); setStage('files'); setMessage(null); } else {
      setError(r.message || 'That code did not work.');
      if (r.code === 'LINK_LOCKED' || r.code === 'LINK_NOT_AVAILABLE') setStage('unavailable');
      // Only this address is locked: another recipient's address can still open the link.
      if (r.code === 'ADDRESS_LOCKED') { setStage('email'); setCode(''); }
    }
  };
  const refresh = useCallback(async () => {
    const r = await post<{ files: FileRow[]; confirmed: boolean }>('/files', { token: token.current, session });
    if (r.ok && r.data) { setFiles(r.data.files); setConfirmed(r.data.confirmed); } else if (r.code === 'SESSION_ENDED' || r.code === 'ADDRESS_LOCKED') { setStage('email'); setError(r.message || null); } else if (r.code === 'LINK_LOCKED' || r.code === 'LINK_NOT_AVAILABLE') { setStage('unavailable'); setError(r.message || null); }
  }, [session]);
  const download = async (f: FileRow) => {
    setError(null);
    const r = await postFile('/download', { token: token.current, session, file_no: f.file_no });
    if (!r.ok || !r.blob) { setError(r.message || 'The file could not be downloaded.'); await refresh(); return; }
    const href = window.URL.createObjectURL(r.blob);
    const a = document.createElement('a');
    a.href = href; a.download = r.name || f.name;
    document.body.appendChild(a); a.click(); a.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(href), 0);
    await refresh();
  };
  const confirm = async () => {
    setBusy(true);
    const r = await post<{ confirmed: boolean }>('/confirm', { token: token.current, session });
    setBusy(false);
    if (r.ok) { setConfirmed(true); setMessage('Thank you. Sounds for Seniors has your confirmation.'); } else setError(r.message || 'That did not work.');
  };

  return (
    <main className="mx-auto w-full max-w-xl px-4 py-8 text-slate-900" aria-labelledby="records-title">
      <h1 id="records-title" className="text-xl font-semibold">Sounds for Seniors visit records</h1>
      {stage === 'loading' && <p role="status" className="mt-4 text-slate-600">Opening…</p>}
      {stage === 'unavailable' && (
        <div className="mt-4 rounded-md border border-slate-200 p-4" role="alert">
          <p>{error || 'This link is not available. It may have expired or been replaced. Ask Sounds for Seniors to send a new one.'}</p>
        </div>
      )}
      {info && stage !== 'unavailable' && (
        <p className="mt-2 text-slate-700">{info.file_count} {info.file_count === 1 ? 'file' : 'files'} from the {usDate(info.visit_date)} visit to {info.building_name}. The link works until {new Date(info.expires_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}.</p>
      )}
      {stage === 'email' && (
        <form onSubmit={askCode} className="mt-6 flex flex-col gap-3" aria-label="Ask for a code">
          <label htmlFor="records-email" className="text-sm font-medium">Your email address (the one these records were sent to)</label>
          <Input id="records-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={254} />
          <Button type="submit" disabled={busy || !email.trim()}>{busy ? 'Sending…' : 'Send me a code'}</Button>
        </form>
      )}
      {stage === 'code' && (
        <form onSubmit={verify} className="mt-6 flex flex-col gap-3" aria-label="Enter the code">
          {message && <p role="status" className="text-slate-700">{message}</p>}
          <label htmlFor="records-code" className="text-sm font-medium">The 6-digit code from the email</label>
          <Input id="records-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
          <Button type="submit" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Open the records'}</Button>
          <Button type="button" variant="ghost" onClick={() => { setStage('email'); setCode(''); setMessage(null); }}>Send a new code</Button>
        </form>
      )}
      {stage === 'files' && (
        <section className="mt-6 flex flex-col gap-3" aria-label="Files">
          <ul className="flex flex-col gap-2">
            {files.map((f) => (
              <li key={f.file_no} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-3">
                <span className="min-w-0 break-words"><strong>{f.name}</strong><br /><span className="text-sm text-slate-600">{f.kind} · {size(f.byte_size)} · {f.available ? `${f.downloads_left} ${f.downloads_left === 1 ? 'download' : 'downloads'} left` : 'not available'}</span></span>
                <Button type="button" variant="outline" disabled={!f.available} onClick={() => download(f)}>Download</Button>
              </li>
            ))}
          </ul>
          {confirmed
            ? <p role="status" className="text-emerald-700">{message || 'Receipt confirmed. Thank you.'}</p>
            : <Button type="button" onClick={confirm} disabled={busy}>Confirm received</Button>}
        </section>
      )}
      {error && stage !== 'unavailable' && <p role="alert" className="mt-4 text-red-700">{error}</p>}
    </main>
  );
}
